import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ExclamationCircleIcon, MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import LoginLegalLinks from './LoginLegalLinks';
import { announceThemeChoice, readThemeChoice, saveThemeChoice, THEME_KEY } from '../utils/theme';
import { EMAIL_FEHLER, emailFehler } from '../administration/organization/feldFehler';
import './LoginShell.css';

function useLoginTheme() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));

  useEffect(() => {
    const root = document.documentElement;
    const meta = document.querySelector('meta[name="theme-color"]');
    const previousColorScheme = root.style.colorScheme;
    const previousThemeColor = meta?.getAttribute('content');
    const syncTheme = () => {
      const isDark = root.classList.contains('dark');
      setDark(isDark);
      root.style.colorScheme = isDark ? 'dark' : 'light';
      meta?.setAttribute('content', isDark ? '#0e1b2b' : '#f4f7fb');
    };
    // Paket 08 Schritt 7: eine Wahl aus einem anderen Tab (auch „System“) wendet main.tsx an.
    const syncStorage = (event: StorageEvent) => {
      if (event.key === THEME_KEY) announceThemeChoice(readThemeChoice());
    };
    const observer = new MutationObserver(syncTheme);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('storage', syncStorage);
    syncTheme();
    return () => {
      observer.disconnect();
      window.removeEventListener('storage', syncStorage);
      root.style.colorScheme = previousColorScheme;
      if (previousThemeColor) meta?.setAttribute('content', previousThemeColor);
    };
  }, []);

  const chooseTheme = (isDark: boolean) => {
    document.documentElement.classList.toggle('dark', isDark);
    setDark(isDark);
    // Über theme.ts, damit main.tsx eine laufende Wahl „System“ abmeldet.
    saveThemeChoice(isDark ? 'dark' : 'light');
  };

  return { dark, chooseTheme };
}

// DR-13 UI-25: die öffentlichen Formulare prüfen selbst (noValidate) und nennen den Fehler am Feld, wie FeldFehler in der
// Verwaltung; ohne Browserblasen in der Sprache des Browsers. Die E-Mail-Prüfung ist dieselbe wie in der Verwaltung.
export type FeldFehlerListe = Record<string, string>;
export const emailPflichtFehler = (text: string): string | undefined => text.trim() ? emailFehler(text) : EMAIL_FEHLER;
/** Nimmt nur gesetzte Fehler auf; die Reihenfolge der Felder bleibt, und das erste fehlerhafte Feld (id = Schlüssel) bekommt den Fokus. */
export function pruefeFelder(pruefung: Record<string, string | undefined>): FeldFehlerListe {
  const fehler = Object.fromEntries(Object.entries(pruefung).filter((eintrag): eintrag is [string, string] => !!eintrag[1]));
  const erstes = Object.keys(fehler)[0];
  if (erstes) document.getElementById(erstes)?.focus();
  return fehler;
}
/** Die Angaben für ein Eingabefeld mit möglichem Fehler; weitere beschreibende ids hängen hinten an. */
export const feldAngaben = (id: string, fehler: FeldFehlerListe, ...beschreibung: Array<string | undefined>) => {
  const ids = [fehler[id] ? `${id}-fehler` : undefined, ...beschreibung].filter(Boolean).join(' ');
  return { id, 'aria-invalid': fehler[id] ? true : undefined, 'aria-describedby': ids || undefined };
};
export function LoginFeldFehler({ id, fehler }: { id: string; fehler: FeldFehlerListe }) {
  return fehler[id] ? <p className="login-field-error" id={`${id}-fehler`} role="alert"><ExclamationCircleIcon aria-hidden="true" />{fehler[id]}</p> : null;
}

/** PF-LG-anmeldung-2: Wortmarke als Text wie in Bennys HTML-Entwurf vom 20.10.2026, „Ai“ und Punkt im Akzentblau. */
function Wortmarke({ className }: { className: string }) {
  return (
    <Link to="/login" className={className} aria-label="CLAiRIS" translate="no">
      CL<span className="login-wordmark-ai">Ai</span>RIS<span className="login-wordmark-period" aria-hidden="true">.</span>
    </Link>
  );
}

/** Bennys HTML-Entwurf vom 20.10.2026 (PF-LG-anmeldung-1/-2): links der pulverblaue Kasten mit Wortmarke, Leitsatz, §-Zeichen und
 *  dem Gesetzbuch mit Lupe, rechts die helle Fläche mit der Anmeldung, unten „Eine Idee von Aidea.“ und die Rechtslinks. */
export default function LoginShell({ children }: { children: ReactNode }) {
  const { dark, chooseTheme } = useLoginTheme();
  const themeLabel = dark ? 'Helle Ansicht aktivieren' : 'Dunkle Ansicht aktivieren';

  return (
    <div className="login-page">
      <a className="login-skip-link" href="#auth-content">Zum Anmeldebereich</a>
      <div className="login-hero">
        <div className="login-brand-row">
          <Wortmarke className="login-wordmark" />
          <span className="login-country">Österreich</span>
        </div>
        <div className="login-hero-copy">
          <span className="login-hero-rule" aria-hidden="true" />
          <p className="login-claim"><span>Österreichisches</span>{' '}<span>Recht.</span>{' '}<span className="login-claim-accent">Klar gesehen.</span></p>
          <p className="login-claim-copy">Ihre Perspektive auf das Recht.<br />Mit KI-gestützter Recherche von <span translate="no">CLAiRIS</span>.</p>
        </div>
        <div className="login-photo" aria-hidden="true" />
        <span className="login-section-mark" aria-hidden="true">§</span>
      </div>
      <main className="login-column">
        <div className="login-tools">
          <Wortmarke className="login-wordmark login-wordmark-mobile" />
          <button type="button" className="login-theme-toggle" aria-label={themeLabel} title={themeLabel} aria-pressed={dark} onClick={() => chooseTheme(!dark)}>
            {dark ? <SunIcon aria-hidden="true" /> : <MoonIcon aria-hidden="true" />}
          </button>
        </div>
        <section id="auth-content" className="login-panel" aria-labelledby="auth-title" tabIndex={-1}>
          {children}
        </section>
        <footer className="login-foot">
          <p className="login-maker">Eine Idee von <strong translate="no">Aidea<span aria-hidden="true">.</span></strong></p>
          <LoginLegalLinks />
        </footer>
      </main>
    </div>
  );
}
